import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api from "@/lib/api";

const PALETTE = ["#002FA7", "#E4572E", "#1F7A3A", "#F2A541", "#6C5B7B", "#2A9D8F", "#8D6E63", "#C2185B", "#455A64", "#9E9D24"];

const naira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
const short = (n) => (n >= 1e6 ? `₦${(n / 1e6).toFixed(1)}m` : n >= 1e3 ? `₦${Math.round(n / 1e3)}k` : `₦${n}`);

// Monthly gross payroll cost per department across the last N payroll runs.
export const DeptCostTrend = ({ months = 12 }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [groupBy, setGroupBy] = useState("department");

    useEffect(() => {
        setData(null);
        setError("");
        api.get("/reports/department-cost-trend", { params: { months, group_by: groupBy } })
            .then(({ data: d }) => setData(d))
            .catch(() => setError("Could not load cost trend"));
    }, [months, groupBy]);
    const label = groupBy === "division" ? "Division" : "Department";

    return (
        <div className="bg-white border border-ink/10 p-6 sm:p-10" data-testid="dept-cost-trend">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="overline">Payroll cost</div>
                    <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink" data-testid="dept-cost-trend-title">
                        {label} cost — last {months} months
                    </h2>
                    <p className="mt-1 text-sm text-ink/60">Gross pay plus other allowance, per {label.toLowerCase()}, for each payroll run.</p>
                </div>
                <div className="inline-flex border border-ink/15" data-testid="dept-cost-trend-toggle">
                    {["department", "division"].map((g) => (
                        <button
                            key={g}
                            onClick={() => setGroupBy(g)}
                            className={`px-4 py-2 text-sm capitalize transition-colors ${groupBy === g ? "bg-klein text-white" : "text-ink/60 hover:text-ink hover:bg-ink/5"}`}
                            data-testid={`dept-cost-trend-by-${g}`}
                        >
                            {g}
                        </button>
                    ))}
                </div>
            </div>
            {error && <div className="mt-6 text-sm text-destructive" data-testid="dept-cost-trend-error">{error}</div>}
            {!data && !error && <div className="mt-6 text-sm text-ink/40">Loading…</div>}
            {data && data.series.length === 0 && (
                <div className="mt-6 text-sm text-ink/50" data-testid="dept-cost-trend-empty">No payroll runs yet.</div>
            )}
            {data && data.series.length > 0 && (
                <div className="mt-8 h-80" data-testid="dept-cost-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.series} margin={{ left: 8, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A14" vertical={false} />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={short} tick={{ fontSize: 12 }} width={64} />
                            <Tooltip formatter={(v) => naira(v)} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {data.departments.map((d, i) => (
                                <Bar key={d} dataKey={d} stackId="cost" fill={PALETTE[i % PALETTE.length]} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default DeptCostTrend;
